import React, { Suspense, useState, JSX } from 'react';
import { MemoryRouter } from 'react-router';
import { useMount } from 'react-use';
import { observer } from 'mobx-react';
import { Theme, ThemeProvider, useAppService, useConfig, useTheme } from '@ibe/components';
import { LoggerFactory } from '@ibe/services';
import App from '../App';
import { boot, initLanguage } from '../Config/config';
import initI18next from '../Translations/setup';
import { getTheme } from '../Util/themeUtils';
import { PageUrl } from '../pages';
import ExternalParams, {
  ExternalParamsContextProps,
  ExternalSearchProps
} from '../Config/ExternalParamsContext';
import { ServiceParamsTransformer } from '../Pages/Params/ServiceParamsTransformer';
import { ExternalSearchWidgetParams, externalSearchWidgetSessionKey } from './IBESearchWidget';
import useCms from '../Hooks/useCms';
import Workflow from '../Models/CMS/Workflow';
import i18next from 'i18next';
import { Spinner } from '../Components/Spinner/Spinner';

const logger = LoggerFactory.get('AppWidget');

export type WidgetProps = ExternalParamsContextProps & {
  defaultUrl: string;
  position?: 'TOP' | 'BOTTOM';
  themeNumber?: number;
} & ExternalSearchProps;

const Fallback = (props: { defaultSpinner?: boolean }): JSX.Element => (
  <div
    className="backgroundContainer"
    style={{
      display: 'flex',
      width: '100%',
      height: '100vh',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'var(--page-background)'
    }}
  >
    <Spinner {...props} />
  </div>
);

const AppWidget = observer((props: WidgetProps) => {
  const {
    widgetSessionKeyPrefix: keyPrefix,
    packageCode,
    hotelCode,
    salesChannel,
    workflow,
    paxDefinition,
    searchParams: ibeSearchParams,
    themeNumber
  } = props;
  const cmsService = useCms();
  const [bootstrappingFinished, setBootstrappingFinished] = useState<boolean>(false);
  const [theme, setTheme] = useState<{} | Theme>(useTheme());
  const [defaultUrl, setDefaultUrl] = useState<string>(
    props.defaultUrl ?? PageUrl.PACKAGE + '?wrk=public'
  );
  const appService = useAppService();
  const config = useConfig();
  const widgetSessionKeyPrefix = keyPrefix || document.body.id || 'prefix';

  useMount(async () => {
    await boot(true, props);
    await initI18next(props);
    const lang = initLanguage(i18next.language, config.defaultLanguage);
    appService.setLang(lang);
    // set currency from widget view to the context ???
    const appTheme = await getTheme();
    setTheme(appTheme);

    let localServiceWidgetParams: ExternalSearchWidgetParams | undefined = undefined;
    if (packageCode && salesChannel && workflow) {
      let localWorkflow: Workflow | undefined = undefined;
      try {
        localWorkflow = await cmsService.fetchPackageWorkflow(workflow);
      } catch (err) {
        logger.error(err);
      }
      localServiceWidgetParams = {
        serviceSearchParams: {
          code: packageCode,
          salesChannelCode: salesChannel,
          wrk: workflow,
          hotelCode,
          ...ibeSearchParams
        },
        searchWidgetParams: {
          defaultAdults: paxDefinition?.defaultAdults || localWorkflow?.numberOfAdults,
          maxAdults: paxDefinition?.maxAdults || localWorkflow?.maxNumberOfAdults,
          maxChildren: paxDefinition?.maxChildren || localWorkflow?.maxNumberOfChildren,
          maxChildAge: paxDefinition?.maxChildAge || localWorkflow?.maxChildAge,
          maxDuration: paxDefinition?.maxDuration || localWorkflow?.maxTripDuration
        }
      };
    }

    const searchWidgetParams = externalSearchWidgetSessionKey.get();
    if (localServiceWidgetParams || searchWidgetParams) {
      const widgetParams = (localServiceWidgetParams ||
        searchWidgetParams) as ExternalSearchWidgetParams;
      const urlParams = ServiceParamsTransformer.encode(
        widgetParams.serviceSearchParams,
        widgetParams.searchWidgetParams
      );
      setDefaultUrl(`${PageUrl.HOTEL_LIST}?${urlParams}`);
      externalSearchWidgetSessionKey.clear();
    }

    setBootstrappingFinished(true);
  });

  return bootstrappingFinished ? (
    <ThemeProvider theme={theme}>
      <div id="iso">
        <Suspense fallback={<Fallback />}>
          <MemoryRouter initialEntries={[defaultUrl]}>
            <ExternalParams {...props} widgetSessionKeyPrefix={widgetSessionKeyPrefix}>
              <App mode="WIDGET" defaultUrl={defaultUrl} externalThemeNumber={themeNumber} />
            </ExternalParams>
          </MemoryRouter>
        </Suspense>
      </div>
    </ThemeProvider>
  ) : (
    <div id="iso">
      <Fallback defaultSpinner />
    </div>
  );
});

export default AppWidget;
